import { Device, PageType } from '@/types/app';
import { InitializedPayment, PaymentConfig, PaymentConfigV2 } from '@/types/payment';
import { getSite } from '@/utils/getSite';
import { getUrl } from '@/utils/getUrl';
import { http } from '@/utils/http';
import { isDev } from '@/utils/isDev';
import { logger } from '@/utils/logger';
import { withBFFApiKeyHeader } from '@/utils/withBFFApiKeyHeader';
import { AxiosError } from 'axios';
import https from 'https';
import { getOcelotConfig } from 'lib/configs/ocelot';
import { getAipBffUrl } from 'lib/paymentsV2/getAipBffUrl';
import { getClientCredentialsToken } from 'lib/paymentsV2/getClientCredentialsToken';
import { SubscriptionDetails } from 'pages/api/payments/v1/subscription-details';

export const initPaymentRequest = async (alltIdUserCode: string) => {
  const response = await http
    .post<InitializedPayment>(
      '/api/payments/v1/create-klarna-session',
      {
        alltIdUserCode,
      },
      { skipInterceptors: { response: { rejected: true } } },
    )
    .catch((error: AxiosError) => {
      if (error?.status === 409) {
        return { status: 409, data: null };
      }
    });

  const { data } = response || {};

  if (response?.status === 409) {
    logger.error(`initPaymentRequest: Cannot initialize payment for user: ${alltIdUserCode}`);
    throw new Error('Could not initialized payment request', {
      cause: { status: 409 },
    });
  }

  if (!data) {
    logger.error(`initPaymentRequest: Cannot initialize payment for user: ${alltIdUserCode}`);
    throw new Error('Could not initialized payment request');
  }

  return data;
};

export const createOrderRequest = async (
  alltIdUserCode: string,
  authorizationToken: string,
  dataAnalytics?: {
    pageId?: number;
    device?: Device;
    type?: PageType;
    publishedUrl?: string;
  },
) => {
  const response = await http.post('/api/payments/v1/create-klarna-order', {
    alltIdUserCode,
    authorizationToken,
    dataAnalytics,
  });

  const { data } = response || {};

  if (!data) {
    logger.error(`createOrderRequest: Cannot create payment order for the user: ${alltIdUserCode}`);
    throw new Error('Could not create order');
  }

  return data;
};

export const getPaymentConfig = async () => {
  const paymentMiddlewareUrl = process.env.PAYMENT_MIDDLEWARE_URL;
  const xApiToken = process.env.PAYMENT_MIDDLEWARE_API_TOKEN;
  const purchaseTag = getOcelotConfig('features.payments.v1.purchaseTag');
  const url = getUrl('/config/payment', paymentMiddlewareUrl);

  if (!purchaseTag) {
    return undefined;
  }

  if (!paymentMiddlewareUrl || !url || !xApiToken) {
    logger.error('Getting payment config: Internal server error');
    return undefined;
  }

  url.searchParams.set('brand', getSite());
  url.searchParams.set('purchaseTag', purchaseTag);

  const paymentConfig = await http.get(url.href, {
    httpsAgent: isDev ? new https.Agent({ rejectUnauthorized: false }) : undefined,
    headers: {
      letmein: 'foriamzeus',
      'X-Api-Token': xApiToken,
    },
  });

  if (!paymentConfig?.data) {
    return undefined;
  }

  return {
    purchaseTag,
    ...paymentConfig.data,
  } as PaymentConfig;
};

type FrisbiiPlanSummary = {
  handle: string;
  name: string;
  amount: number;
  currency: string;
  state: 'active' | 'superseded' | 'deleted';
  metadata?: { close_time?: string };
};

export const getPaymentConfigV2 = async (): Promise<PaymentConfigV2 | undefined> => {
  const { defaultPlan } = getOcelotConfig('features.payments.v2');

  if (!defaultPlan) {
    return undefined;
  }

  try {
    const serviceToken = await getClientCredentialsToken().catch((error) => {
      logger.error('getPaymentConfigV2: Client credentials token request failed', error);
      return null;
    });

    if (!serviceToken) {
      logger.error('getPaymentConfigV2: Cannot get client credentials token (check AIP auth env and BFF client scopes)');
      return undefined;
    }

    const plansUrl = new URL('/v1/payment/plans', getAipBffUrl());
    plansUrl.searchParams.set('prefix', defaultPlan);

    const response = await http
      .get<{ plans: FrisbiiPlanSummary[] }>(plansUrl.href, {
        skipInterceptors: { response: { rejected: true } },
        headers: {
          Authorization: `Bearer ${serviceToken}`,
          ...withBFFApiKeyHeader(),
        },
      })
      .catch((error: AxiosError) => {
        logger.error(`getPaymentConfigV2: Plans request failed with status ${error.response?.status}`, error.response?.data);
        return null;
      });

    const plan = response?.data?.plans?.find(({ handle }) => handle === defaultPlan);

    if (!plan) {
      logger.error(`getPaymentConfigV2: Plan not found: ${defaultPlan}`);
      return undefined;
    }

    const closeTimeMetadata = plan.metadata?.close_time;
    const closeTime = closeTimeMetadata && !Number.isNaN(Date.parse(closeTimeMetadata)) ? closeTimeMetadata : undefined;

    if (closeTimeMetadata && !closeTime) {
      logger.error(
        `getPaymentConfigV2: Ignoring unparseable close_time metadata on plan ${plan.handle}: ${closeTimeMetadata}`,
      );
    }

    return {
      enabled: plan.state === 'active',
      plan: plan.handle,
      data: {
        amount: plan.amount,
        currency: plan.currency,
        name: plan.name,
        closeTime,
      },
    };
  } catch (error) {
    logger.error('getPaymentConfigV2: Failed to resolve payment config', error);
    return undefined;
  }
};

export const getUserSubscriptionDetails = async (alltidCC: string) => {
  const paymentMiddlewareUrl = process.env.PAYMENT_MIDDLEWARE_URL;
  const xApiToken = process.env.PAYMENT_MIDDLEWARE_API_TOKEN;

  if (!paymentMiddlewareUrl || !xApiToken) {
    logger.error('Getting user subscription details: Payment middleware configuration is missing');
    throw Error('Payment middleware configuration is missing');
  }

  const response = await http.get<SubscriptionDetails[]>(
    `${paymentMiddlewareUrl}/subscription/details?${new URLSearchParams({ alltidCC }).toString()}`,
    {
      headers: {
        letmein: 'foriamzeus',
        'X-Api-Token': xApiToken,
      },
    },
  );

  return response?.data;
};
